import { TimelineStatusLevel } from '@codezo/graphql';
import { cn, formatDate } from '../../lib/utils';

interface DeadlineBadgeProps {
	deadline: string;
	showDate?: boolean;
	/** Leading label inside the pill (e.g. "Accept by") to name which deadline. */
	label?: string;
	/**
	 * Neutral coloring regardless of status — for surfaces where the deadline is
	 * informational (e.g. a client's requested date) rather than an actionable
	 * status. Keeps the countdown + date text; drops the traffic-light color.
	 */
	muted?: boolean;
	/**
	 * Backend-authoritative timeline status. When present the badge colors on
	 * `level` — the countdown merged with feasibility, so it's only green when an
	 * offer can actually hit the deadline — and counts business days. Falls back
	 * to a plain calendar countdown when omitted (surfaces not yet backend-wired).
	 */
	status?: {
		level: TimelineStatusLevel;
		businessDaysLeft?: number | null;
		isExpired: boolean;
	} | null;
}

const LEVEL_CLASS: Record<TimelineStatusLevel, string> = {
	[TimelineStatusLevel.OnTrack]: 'bg-status-success/16 text-status-success',
	[TimelineStatusLevel.AtRisk]: 'bg-status-warn/16 text-status-warn',
	[TimelineStatusLevel.Critical]: 'bg-status-danger/16 text-status-danger',
	// Neutral — never green — for no deadline / not-yet-assessable.
	[TimelineStatusLevel.Unknown]: 'bg-surface-fill text-text-3',
};

export function DeadlineBadge({
	deadline,
	showDate = true,
	label,
	muted = false,
	status,
}: DeadlineBadgeProps) {
	let colorClass: string;
	let text: string;

	if (status) {
		const n = status.businessDaysLeft;
		// A live deadline is worth seeing even before feasibility can judge it: UNKNOWN
		// with days still on the clock is a real date, not the "no deadline" case the
		// neutral fill is for. Anything the backend has actually judged keeps its level.
		colorClass =
			status.level === TimelineStatusLevel.Unknown && !status.isExpired && n != null
				? 'bg-accent-soft text-accent'
				: LEVEL_CLASS[status.level];
		text = status.isExpired
			? `Expired${n != null ? ` ${Math.abs(n)}d ago` : ''}`
			: n === 0
				? 'Due today'
				: n != null
					? `${n}d left`
					: 'Scheduled';
	} else {
		const diffDays = Math.ceil(
			(new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
		);
		const isExpired = diffDays < 0;
		const isUrgent = diffDays >= 0 && diffDays <= 3;
		colorClass = isExpired
			? 'bg-status-danger/16 text-status-danger'
			: isUrgent
				? 'bg-status-warn/16 text-status-warn'
				: 'bg-accent-soft text-accent';
		text = isExpired
			? `Expired ${Math.abs(diffDays)}d ago`
			: diffDays === 0
				? 'Expires today'
				: `${diffDays}d left`;
	}

	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium',
				muted ? 'bg-surface-fill text-text-3' : colorClass
			)}
		>
			{label && <span className="font-normal opacity-70">{label}</span>}
			<span>{text}</span>
			{showDate && (
				<>
					<span className="opacity-60">·</span>
					<span className="font-normal opacity-70">{formatDate(deadline)}</span>
				</>
			)}
		</span>
	);
}
